import React, { useState, useRef } from "react";
import { ArrowRight, ShieldAlert, Send, Lock as LockIcon, Mic, Square, Loader2, ScanText } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAiConfirm } from "@/components/AiConfirm";
import { Justice } from "@/components/Justice";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const BOXES = [
  { key: "original", label: "ORIGINAL ENTRY", color: "var(--ek-steel)" },
  { key: "fact_only", label: "FACT-ONLY VERSION", color: "var(--ek-secure)" },
  { key: "needs_support", label: "NEEDS SUPPORT", color: "var(--ek-warning)" },
  { key: "interpretation", label: "PERSONAL INTERPRETATION", color: "var(--ek-child)" },
];

export default function AICoach({ open, onOpenChange, onSaveEntry }) {
  const { confirmAi } = useAiConfirm();
  const [note, setNote] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [scanning, setScanning] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const imgRef = useRef(null);

  const refine = async () => {
    if (!note.trim()) return;
    const ok = await confirmAi({
      action: "fact_filter",
      title: "Refine with Justice",
      description: "Justice will separate the facts from interpretation in this entry and flag anything that needs supporting evidence.",
    });
    if (!ok) return;
    setBusy(true); setResult(null);
    try { const { data } = await api.post("/coach/refine", { note }); setResult(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Justice is temporarily unavailable"); }
    finally { setBusy(false); }
  };

  const uploadRecording = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "note.webm");
      const { data } = await api.post("/coach/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const text = (data.text || "").trim();
      if (!text) { toast.message("No speech detected. Try again."); return; }
      setNote((prev) => (prev ? `${prev} ${text}` : text));
      toast.success("Transcribed — review and refine");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transcription failed");
    } finally {
      setTranscribing(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        setRecording(false);
        if (blob.size > 0) uploadRecording(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Microphone access was denied");
    }
  };

  const stopRecording = () => { try { mediaRef.current?.stop(); } catch {} };
  const toggleRecording = () => (recording ? stopRecording() : startRecording());

  const scanScreenshot = async (fileObj) => {
    if (!fileObj) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const { data } = await api.post("/coach/ocr-date", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parts = [];
      if (data.suggested_date) parts.push(`On ${data.suggested_date}${data.suggested_time ? ` at ${data.suggested_time}` : ""}:`);
      if (data.summary) parts.push(data.summary);
      const snippet = parts.join(" ") || data.extracted_text || "";
      if (snippet) setNote((prev) => (prev ? `${prev}\n${snippet}` : snippet));
      toast.success(data.suggested_date ? `Suggested date: ${data.suggested_date}` : "Screenshot read — review the text");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read that screenshot");
    } finally {
      setScanning(false);
      if (imgRef.current) imgRef.current.value = "";
    }
  };

  const sendToEntry = () => {
    const text = (result?.fact_only || []).join(" ");
    if (!text) { toast.error("No facts to send"); return; }
    onSaveEntry && onSaveEntry(text);
    setNote(""); setResult(null);
  };

  const renderBox = (b) => {
    const content = b.key === "original" ? [result.original] : result[b.key];
    return (
      <div key={b.key} className="border rounded-lg p-3" style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)" }} data-testid={`box-${b.key}`}>
        <div className="flex items-center justify-between mb-2">
          <span className="ek-stamp" style={{ color: b.color, borderColor: "var(--ek-border)" }}>{b.label}</span>
          {b.key === "original" && <LockIcon className="h-3 w-3" style={{ color: "var(--ek-steel)" }} />}
        </div>
        {(!content || content.length === 0) ? (
          <p className="text-xs" style={{ color: "var(--ek-text-muted)" }}>—</p>
        ) : b.key === "original" ? (
          <p className="text-sm leading-relaxed" data-private>{content[0]}</p>
        ) : (
          <ul className="space-y-1.5">
            {content.map((c, i) => <li key={i} className="text-sm leading-relaxed" style={{ color: "var(--ek-text-secondary)" }} data-private>• {c}</li>)}
          </ul>
        )}
      </div>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto border-l p-6" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
        <SheetHeader className="text-left">
          <div className="flex items-center gap-3 mb-2">
            <Justice size={56} />
            <div>
              <span className="ek-stamp">DOCUMENTATION COACH</span>
              <SheetTitle className="font-head font-semibold tracking-tight text-2xl mt-1">Justice</SheetTitle>
            </div>
          </div>
          <SheetDescription style={{ color: "var(--ek-text-secondary)" }}>
            Tell me what happened in your own words. I'll separate feelings from facts — and keep your original entry preserved.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-[11px] leading-relaxed" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)", color: "var(--ek-text-muted)" }} data-testid="ai-legal-disclaimer">
          <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "var(--ek-warning)" }} />
          <span><b style={{ color: "var(--ek-text-secondary)" }}>Not legal advice.</b> Justice is an AI documentation assistant, not a lawyer. AI-assisted output should be reviewed by you and qualified counsel.</span>
        </div>

        <div className="mt-6 space-y-4">
          <Textarea data-testid="coach-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={5} className="bg-transparent resize-none" placeholder="e.g. He showed up 40 minutes late again and screamed at me..." />

          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={toggleRecording}
              disabled={transcribing}
              data-testid="coach-record-btn"
              variant="outline"
              className="rounded-md border shrink-0"
              style={{ borderColor: recording ? "rgba(240,97,109,0.5)" : "var(--ek-border)", background: recording ? "rgba(240,97,109,0.1)" : "transparent" }}
            >
              {transcribing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" style={{ color: "var(--ek-primary)" }} /> Transcribing…</>
              ) : recording ? (
                <><Square className="h-4 w-4 mr-2 ek-pulse" style={{ color: "var(--ek-error)" }} /> Stop &amp; transcribe</>
              ) : (
                <><Mic className="h-4 w-4 mr-2" style={{ color: "var(--ek-primary)" }} /> Speak it</>
              )}
            </Button>
            <input ref={imgRef} type="file" accept="image/*" className="hidden" data-testid="coach-screenshot-input" onChange={(e) => e.target.files?.[0] && scanScreenshot(e.target.files[0])} />
            <Button type="button" onClick={() => imgRef.current?.click()} disabled={scanning} data-testid="coach-scan-btn" variant="outline" className="rounded-md border shrink-0" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
              {scanning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" style={{ color: "var(--ek-primary)" }} /> Reading…</> : <><ScanText className="h-4 w-4 mr-2" style={{ color: "var(--ek-primary)" }} /> Scan screenshot</>}
            </Button>
            <span className="text-xs" style={{ color: "var(--ek-text-muted)" }}>
              {recording ? "Listening… tap stop when done." : "Voice or a message screenshot — Justice reads it & suggests a date (1 analysis)."}
            </span>
          </div>

          <Button onClick={refine} disabled={busy || transcribing || !note.trim()} data-testid="coach-analyze-btn" className="w-full rounded-md group" style={{ background: "var(--ek-primary)", color: "#fff" }}>
            {busy ? "SEPARATING FACTS..." : "Separate feelings from facts"}
            {!busy && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
          </Button>

          {result && (
            <div className="space-y-3" data-testid="coach-result">
              {BOXES.map(renderBox)}
              <Button onClick={sendToEntry} data-testid="coach-send-entry-btn" variant="outline" className="w-full rounded-md border" style={{ borderColor: "rgba(46,130,246,0.4)", background: "transparent", color: "var(--ek-primary)" }}>
                <Send className="h-4 w-4 mr-2" /> Send Fact-Only to a new timeline entry
              </Button>
            </div>
          )}

          <div className="flex items-start gap-2 text-xs pt-2" style={{ color: "var(--ek-text-muted)" }}>
            <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" /> I can help you document, but I cannot provide legal advice.
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
